import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  WatchRunnerSet,
  type WatchEvent,
  type WatchView,
  type WatchViewSnapshot,
} from '@logwatch-core';
import { useLogcatChunks } from '@/lib/logcat/useLogcatStream';
import { convertLogLine } from './convertLogLine';
import { patternFromKey } from './builder';
import type { LogLine } from '@/lib/logcat/parser';
import { useDevicePrefs, useDevicePrefsUpdate } from '@/lib/myPrefs';

/**
 * useDeviceKeyWatchers —— 同一台设备的所有 logcat tab 共享一份 keys 列表。
 *
 * 阶段 A：keys 持久化迁到 B2 prefs.devices[serial].logWatch.keys，跟账号走、
 * 跨电脑跨浏览器自动同步（debounced PUT + 下一次 prefs poll 拉回来）。
 *
 * 行为：
 *   - 内部维护一个固定的 WatchView（id=`__keys__`），watchers 数组等于
 *     "用户加过的 key"；keys 变化时整体重建 RunnerSet 单 runner 并 replay
 *     `historyLines`，确保新加的 key 立刻能在已有 buffer 上看到命中；
 *   - logcat 共享流被 `clear()` 时同步重建；
 *   - 与 checkpoint 共享行号空间（lineId 来自 LogLine.id）。
 */

const FIXED_VIEW_ID = '__keys__';
// 仅作为内部 WatchView.name 字段保留，不直接渲染给用户；之所以跟着改名
// 是为了运行时数据与 UI 概念保持一致（grep / 调试时能对应上「取值规则」）。
const FIXED_VIEW_NAME = '取值规则';

export interface KeyWatchController {
  /** 按用户添加顺序保留；同名 key 自动去重。 */
  keys: string[];
  /** 添加；空字符串 / 已存在的 key 直接忽略。 */
  addKey: (key: string) => void;
  removeKey: (key: string) => void;
  clearKeys: () => void;
  /** 单 view snapshot；keys 为空时为 undefined。 */
  snapshot?: WatchViewSnapshot;
  /** 所有 watcher 的 count 累加。 */
  totalHits: number;
  /** 当前 RunnerSet 已分配到的 lineId 上界（与 checkpoint 共享行号空间）。 */
  nextLineId: number;
}

/* -------------------- 内部 helper -------------------- */

function buildView(keys: string[]): WatchView {
  return {
    schemaVersion: 1,
    id: FIXED_VIEW_ID,
    name: FIXED_VIEW_NAME,
    watchers: keys.map((k) => ({
      id: keyToWatcherId(k),
      name: k,
      kind: 'extract',
      pattern: patternFromKey(k),
      caseInsensitive: true,
    })),
  };
}

/**
 * 把"键名"转成 watcher.id：合法 ASCII id 字符直接用（小写化），否则用
 * base64 编码避免与其它 key 冲突；watcher.id 只用作 React key 与 snapshot
 * 反查，UI 拿 `name` 渲染。
 */
const ID_RE = /^[A-Za-z0-9_.\-]+$/;
export function keyToWatcherId(key: string): string {
  const trimmed = key.trim();
  if (ID_RE.test(trimmed)) return trimmed.toLowerCase();
  if (typeof globalThis.btoa === 'function') {
    return `k-${globalThis.btoa(unescape(encodeURIComponent(trimmed))).replace(/=+$/, '')}`;
  }
  return `k-${trimmed.replace(/\s+/g, '_')}`;
}

/* -------------------- Hook -------------------- */

export function useDeviceKeyWatchers(opts: {
  /** undefined / 离线时引擎不喂 line，但 keys 状态保留（按 serial 落到 prefs）。 */
  serial: string | undefined;
  /** 当前可见 logcat 行缓冲；keys 变更时回放这部分历史。 */
  historyLines?: LogLine[];
  nowMs?: () => number;
}): KeyWatchController {
  const { serial, historyLines, nowMs } = opts;

  // serial undefined 时仍允许有一个 fallback 持久化桶，避免本地操作丢失。
  const persistKey = serial ?? '__no_serial__';

  const persistedKeys = useDevicePrefs(persistKey, (d) => d.logWatch.keys);
  const updateDevice = useDevicePrefsUpdate(persistKey);

  const keys = useMemo(
    () => persistedKeys.filter((x): x is string => typeof x === 'string' && !!x.trim()),
    [persistedKeys],
  );

  const setKeys = useCallback(
    (next: string[] | ((prev: string[]) => string[])) => {
      const resolved = typeof next === 'function' ? next(keys) : next;
      updateDevice({ logWatch: { keys: resolved } });
    },
    [keys, updateDevice],
  );

  const runnerSetRef = useRef<WatchRunnerSet>();
  const runIdRef = useRef<string>('');
  const historyRef = useRef<LogLine[] | undefined>(historyLines);
  historyRef.current = historyLines;

  const [snapshot, setSnapshot] = useState<WatchViewSnapshot | undefined>(undefined);
  const [nextLineId, setNextLineId] = useState(0);

  // 每当 keys / serial 变化时整体重建 runner（M2 简化：不做 diff，重建成本
  // 取决于 historyLines 长度，与之前 mount 单视图一致）。
  useEffect(() => {
    runnerSetRef.current = new WatchRunnerSet();
    if (keys.length === 0) {
      setSnapshot(undefined);
      setNextLineId(0);
      return;
    }
    const fallback = (nowMs ?? Date.now)();
    const view = buildView(keys);
    const runId = `w-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`;
    runIdRef.current = runId;
    runnerSetRef.current.add({ runId, view, startedAt: fallback });
    const hist = historyRef.current;
    if (hist && hist.length > 0) {
      for (const line of hist) {
        try {
          runnerSetRef.current.feed(convertLogLine(line, fallback));
        } catch {
          /* skip */
        }
      }
    }
    const snaps = runnerSetRef.current.snapshot();
    setSnapshot(snaps[0]);
    setNextLineId(runnerSetRef.current.getNextLineId());
  }, [keys, serial, nowMs]);

  // hit 事件 → 节流刷 snapshot
  useEffect(() => {
    const current = runnerSetRef.current;
    if (!current) return;
    let pending = false;
    const off = current.on((_ev: WatchEvent) => {
      if (pending) return;
      pending = true;
      setTimeout(() => {
        pending = false;
        const cur = runnerSetRef.current;
        if (!cur) return;
        const snaps = cur.snapshot();
        setSnapshot(snaps[0]);
        setNextLineId(cur.getNextLineId());
      }, 100);
    });
    return () => off();
  }, [serial, keys]);

  // 实时喂入 chunks
  useLogcatChunks(serial, (chunk: LogLine[], cleared: boolean) => {
    const current = runnerSetRef.current;
    if (!current) return;
    if (cleared) {
      const fresh = new WatchRunnerSet();
      if (keys.length > 0) {
        const runId = `w-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`;
        runIdRef.current = runId;
        fresh.add({ runId, view: buildView(keys) });
      }
      runnerSetRef.current = fresh;
      setSnapshot(fresh.snapshot()[0]);
      setNextLineId(0);
      return;
    }
    if (chunk.length === 0) return;
    const fallback = (nowMs ?? Date.now)();
    for (const line of chunk) {
      current.feed(convertLogLine(line, fallback));
    }
    setNextLineId(current.getNextLineId());
  });

  /* -------------------- 操作 -------------------- */

  const addKey = useCallback(
    (key: string) => {
      const trimmed = key.trim();
      if (!trimmed) return;
      if (keys.includes(trimmed)) return;
      setKeys([...keys, trimmed]);
    },
    [keys, setKeys],
  );
  const removeKey = useCallback(
    (key: string) => {
      setKeys(keys.filter((k) => k !== key));
    },
    [keys, setKeys],
  );
  const clearKeys = useCallback(() => {
    setKeys([]);
  }, [setKeys]);

  const totalHits = useMemo(
    () => snapshot?.watchers.reduce((sum, w) => sum + w.count, 0) ?? 0,
    [snapshot],
  );

  return useMemo<KeyWatchController>(
    () => ({ keys, addKey, removeKey, clearKeys, snapshot, totalHits, nextLineId }),
    [keys, addKey, removeKey, clearKeys, snapshot, totalHits, nextLineId],
  );
}
